import type { FocusEvent, MouseEvent, RefObject, SyntheticEvent } from "react";
import { useEffect, useRef, useState } from "react";

/**
 * A lazy tooltip's life: `idle` until it first opens (MUI's Tooltip isn't mounted), then `open` or `closed`. It stays
 * mounted once closed, so its exit transition plays and a second hover doesn't mount it again.
 */
export type LazyTooltipPhase = "idle" | "open" | "closed";

export type LazyTooltipTiming = { enterDelay: number; leaveDelay: number; enterTouchDelay: number; leaveTouchDelay: number };

type Params = {
	timing: LazyTooltipTiming;
	/** False for an empty title, which MUI never shows either. */
	canOpen: boolean;
	anchorRef: RefObject<HTMLElement | null>;
	/** The popup's id, to tell "the pointer left for the tooltip itself" from "the pointer left". */
	popperId: string;
};

/**
 * After a touch, the mouse events the browser emulates for it are ignored for this long past the close, as MUI does
 * (its `theme.transitions.duration.shortest`), so a tap doesn't also open the tooltip as a hover.
 */
const TOUCH_MOUSE_GRACE_MS = 150;

const isInside = (container: Element | null, target: unknown): boolean => target instanceof Node && container !== null && container.contains(target);

/**
 * MUI Tooltip's triggers, run on the real anchor so the Tooltip itself can mount late: hover opens after `enterDelay`
 * and leaving closes after `leaveDelay`; keyboard focus (`:focus-visible`, not a click) opens and blur closes; a long
 * press opens after `enterTouchDelay` and lifting closes after `leaveTouchDelay`. The popup's own listeners, which MUI
 * still attaches, report back through `onPopupOpen`/`onPopupClose`: hovering the popup keeps it open, and Escape closes it.
 */
export const useLazyTooltipTriggers = ({ timing, canOpen, anchorRef, popperId }: Params) => {
	const [phase, setPhase] = useState<LazyTooltipPhase>("idle");
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const touchGrace = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const touched = useRef(false);

	useEffect(
		() => () => {
			clearTimeout(timer.current);
			clearTimeout(touchGrace.current);
		},
		[],
	);

	/** Closing a tooltip that never opened leaves it unmounted. */
	const schedule = (next: "open" | "closed", delay: number) => {
		clearTimeout(timer.current);
		if (next === "open" && !canOpen) return;
		timer.current = setTimeout(() => setPhase((current) => (next === "closed" && current === "idle" ? "idle" : next)), delay);
	};

	const onMouseOver = () => {
		if (!touched.current) schedule("open", timing.enterDelay);
	};
	const onMouseLeave = (event: MouseEvent<HTMLElement>) => {
		// Onto the open popup: it stays open, and the popup's own listener closes it when the pointer leaves that too.
		if (isInside(document.getElementById(popperId), event.relatedTarget)) clearTimeout(timer.current);
		else schedule("closed", timing.leaveDelay);
	};
	const onFocus = (event: FocusEvent<HTMLElement>) => {
		if (event.target.matches(":focus-visible")) schedule("open", timing.enterDelay);
	};
	const onBlur = () => schedule("closed", timing.leaveDelay);
	const onTouchStart = () => {
		clearTimeout(touchGrace.current);
		touched.current = true;
		schedule("open", timing.enterTouchDelay);
	};
	const onTouchEnd = () => {
		schedule("closed", timing.leaveTouchDelay);
		touchGrace.current = setTimeout(() => {
			touched.current = false;
		}, timing.leaveTouchDelay + TOUCH_MOUSE_GRACE_MS);
	};

	const onPopupOpen = () => schedule("open", 0);
	const onPopupClose = (event: Event | SyntheticEvent) => {
		// The pointer went from the popup back onto the anchor: the anchor still has it, so the tooltip stays.
		if ("relatedTarget" in event && isInside(anchorRef.current, event.relatedTarget)) return;
		clearTimeout(timer.current);
		setPhase((current) => (current === "idle" ? "idle" : "closed"));
	};

	return { phase, anchorListeners: { onMouseOver, onMouseLeave, onFocus, onBlur, onTouchStart, onTouchEnd }, onPopupOpen, onPopupClose };
};
